import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  organizationService,
  type OrganizationCreate,
  type OrganizationUpdate,
  type MemberInvite,
  type MemberUpdate,
} from '@/services/organization'

// Get organization
export const useOrganization = (organizationId: string) => {
  return useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => organizationService.getOrganization(organizationId),
    enabled: !!organizationId,
  })
}

// Create organization
export const useCreateOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: OrganizationCreate) =>
      organizationService.createOrganization(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      queryClient.setQueryData(['organization', data.organization_id], data)
    },
  })
}

// Update organization
export const useUpdateOrganization = (organizationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: OrganizationUpdate) =>
      organizationService.updateOrganization(organizationId, data),
    onSuccess: (data) => {
      queryClient.setQueryData(['organization', organizationId], data)
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
    },
  })
}

// Delete organization
export const useDeleteOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (organizationId: string) =>
      organizationService.deleteOrganization(organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
    },
  })
}

// List members
export const useMembers = (organizationId: string) => {
  return useQuery({
    queryKey: ['members', organizationId],
    queryFn: () => organizationService.listMembers(organizationId),
    enabled: !!organizationId,
  })
}

// Invite member
export const useInviteMember = (organizationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: MemberInvite) =>
      organizationService.inviteMember(organizationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', organizationId] })
    },
  })
}

// Update member role
export const useUpdateMemberRole = (
  organizationId: string,
  memberId: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: MemberUpdate) =>
      organizationService.updateMemberRole(organizationId, memberId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', organizationId] })
    },
  })
}

// Remove member
export const useRemoveMember = (organizationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) =>
      organizationService.removeMember(organizationId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', organizationId] })
    },
  })
}

